import React, { useCallback, useEffect, useState } from 'react';
import { Card, Badge, Button, Form, Row, Col, Modal } from 'react-bootstrap';
import API from '../services/api';
import { dateTime } from './adminShared';
import { formatDay } from '../utils/dates';

// Administración → Servicios API (owner only): the paid services the apps run on, so none of them stops for lack of
// credit or an unpaid invoice. Balances of prepaid services (Claude, OpenAI) are ESTIMATES: the last top-up recorded
// here minus the spend each provider reports — none of them exposes the real balance. The backend is
// services/apiServices; the alerts are "Servicios API" in Notificaciones → Mis notificaciones.
const usd = (n) => (n == null ? '—' : `US$${Number(n).toFixed(2)}`);
const STATUS = {
  ok: { bg: 'success', text: 'Bien' },
  low: { bg: 'warning', text: 'Saldo bajo' },
  out: { bg: 'danger', text: 'Sin crédito' },
  invoice: { bg: 'danger', text: 'Factura por pagar' },
  error: { bg: 'secondary', text: 'No se pudo consultar' },
  unconfigured: { bg: 'secondary', text: 'Sin conectar' },
};

const Line = ({ label, children }) => (
  <div className="d-flex justify-content-between small py-1" style={{ borderTop: '1px solid var(--mm-line)' }}>
    <span className="text-muted">{label}</span><span className="text-end">{children}</span>
  </div>
);

const ServiceCard = ({ s, busy, onTopup, onSettings, onResolve }) => {
  const last = s.last || {};
  const st = STATUS[last.status] || STATUS.unconfigured;
  const b = last.balance;
  return (
    <Card className="shadow-sm p-3 h-100">
      <div className="d-flex justify-content-between align-items-start mb-1">
        <div>
          <div style={{ fontWeight: 600 }}>{s.name}</div>
          <div className="small text-muted">{s.usedBy}</div>
        </div>
        <Badge bg={st.bg}>{st.text}</Badge>
      </div>

      {s.outOfCredit && (
        <div className="alert alert-danger small py-2 my-2">
          <strong>Rechazó una llamada el {dateTime(s.outOfCredit.at)}:</strong> {s.outOfCredit.message}
          {!s.prepaid && <div className="mt-1"><Button size="sm" variant="outline-light" disabled={busy} onClick={onResolve}>Ya lo resolví</Button></div>}
          {s.prepaid && <div className="mt-1">Al registrar la recarga se quita este aviso.</div>}
        </div>
      )}

      {s.prepaid && (
        <div className="my-2">
          <div className="small text-muted">Saldo estimado</div>
          <div style={{ fontSize: '1.6rem', fontWeight: 700, color: b && b.estimated <= s.lowBalanceAt ? 'var(--mm-red)' : 'var(--mm-cyan)' }}>
            {b ? usd(b.estimated) : '—'}
          </div>
          <div className="small text-muted">
            {b
              ? <>Recarga de {usd(b.topupAmount)} el {formatDay(b.topupAt)} − gastado desde entonces{b.daysLeft != null ? <> · alcanza para ~{Math.floor(b.daysLeft)} días</> : null}</>
              : s.topup
                ? <>Recarga de {usd(s.topup.amount)} el {formatDay(s.topup.at)} registrada; el saldo aparece en cuanto {s.name} informe el gasto.</>
                : 'Registra tu última recarga para estimar cuánto queda.'}
          </div>
        </div>
      )}

      {last.spend && (
        <>
          <Line label="Gastado este mes">{usd(last.spend.monthToDate)}</Line>
          <Line label="Últimos 7 días">{usd(last.spend.last7Days)} <span className="text-muted">({usd(last.spend.dailyAverage)}/día)</span></Line>
        </>
      )}
      {last.invoices?.pending && (
        <Line label="Acumulado este mes (se cobra al cierre)">
          {usd(last.invoices.pending.usd)}{last.invoices.pending.periodEnd ? <span className="text-muted"> · cierra {String(last.invoices.pending.periodEnd).slice(0, 10)}</span> : null}
        </Line>
      )}
      {(last.invoices?.unpaid || []).map(inv => (
        <Line key={inv.id} label={<span className="text-danger">Factura {inv.status === 'FAILED' ? '(cobro fallido)' : 'por pagar'}</span>}>
          <span className="text-danger">{usd(inv.usd)}</span>
        </Line>
      ))}
      {last.extra?.templates && (
        <Line label="Plantillas de WhatsApp">{last.extra.templates.approved} aprobadas{last.extra.templates.pending ? `, ${last.extra.templates.pending} en revisión` : ''}{last.extra.templates.rejected ? `, ${last.extra.templates.rejected} rechazadas` : ''}</Line>
      )}
      {last.stale && <div className="small text-muted mt-2">Cifras de la consulta del {dateTime(last.stale.since)}: {s.name} limitó las consultas por un momento; se vuelve a intentar sola.</div>}
      {last.status === 'error' && <div className="small text-warning mt-2">{last.error}</div>}
      {last.status === 'unconfigured' && <div className="small text-muted mt-2">Para leer el gasto: {s.setup}</div>}

      <div className="d-flex flex-wrap gap-2 mt-3" style={{ gap: 8 }}>
        {s.prepaid && <Button size="sm" disabled={busy} onClick={onTopup}><i className="bi bi-plus-lg me-1" />Registrar recarga</Button>}
        {s.prepaid && <Button size="sm" variant="outline-secondary" disabled={busy} onClick={onSettings}>Avisarme cuando…</Button>}
        <a className="btn btn-sm btn-outline-primary" href={s.billingUrl} target="_blank" rel="noreferrer">Facturación <i className="bi bi-box-arrow-up-right" /></a>
      </div>
      <div className="small text-muted mt-2">{last.checkedAt ? `Consultado ${dateTime(last.checkedAt)}` : 'Aún no consultado.'}</div>
    </Card>
  );
};

const AdminApiServicesTab = () => {
  const [services, setServices] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [topupFor, setTopupFor] = useState(null);
  const [settingsFor, setSettingsFor] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => API.get('/api-services').then(r => setServices(r.data.services))
    .catch(err => setError(err.response?.data?.message || 'No se pudieron leer los servicios.')), []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => {
    setBusy(true); setError(''); setNotice('');
    try { const { data } = await fn(); setServices(data.services); if (data.message) setNotice(data.message); return true; }
    catch (err) { setError(err.response?.data?.message || 'La acción falló.'); return false; }
    finally { setBusy(false); }
  };

  if (!services) return error ? <div className="alert alert-danger">{error}</div> : <div className="text-muted">Cargando servicios…</div>;

  // Today in the Dominican Republic (UTC−4) — toISOString() alone is UTC, already "tomorrow" after 8 p.m. here.
  const today = new Date(Date.now() - 4 * 3600000).toISOString().slice(0, 10);
  return (
    <>
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div className="small text-muted" style={{ maxWidth: 760 }}>
          Los servicios de pago de los que dependen las apps. Ninguno de ellos informa el saldo real por su API: para Claude y OpenAI
          el saldo se <strong>estima</strong> con tu última recarga menos lo que el proveedor reporta gastado desde entonces. Si un servicio
          rechaza una llamada por falta de crédito, lo verás aquí al momento. Los avisos (saldo bajo, facturas) se eligen en
          Notificaciones → Mis notificaciones.
        </div>
        <Button size="sm" variant="outline-primary" disabled={busy} onClick={() => run(() => API.post('/api-services/refresh'))}>
          <i className="bi bi-arrow-clockwise me-1" />{busy ? 'Consultando…' : 'Consultar ahora'}
        </Button>
      </div>
      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className="alert alert-success">{notice}</div>}
      <Row>
        {services.map(s => (
          <Col key={s.key} lg={6} xl={3} className="mb-3">
            <ServiceCard
              s={s} busy={busy}
              onTopup={() => { setForm({ amount: '', at: today, note: '' }); setTopupFor(s); }}
              onSettings={() => { setForm({ lowBalanceAt: s.lowBalanceAt, lowDaysAt: s.lowDaysAt }); setSettingsFor(s); }}
              onResolve={() => run(() => API.delete(`/api-services/${s.key}/out-of-credit`))}
            />
          </Col>
        ))}
      </Row>

      <Modal show={!!topupFor} onHide={() => setTopupFor(null)} centered>
        <Modal.Header closeButton><Modal.Title>Recarga — {topupFor?.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          <div className="small text-muted mb-2">Lo que cargaste (el total de crédito que quedó después de recargar, si lo sabes; si no, el monto de la recarga).</div>
          <Form.Group className="mb-2"><Form.Label>Monto (US$)</Form.Label>
            <Form.Control type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} /></Form.Group>
          <Form.Group className="mb-2"><Form.Label>Fecha</Form.Label>
            <Form.Control type="date" max={today} value={form.at} onChange={e => setForm(f => ({ ...f, at: e.target.value }))} /></Form.Group>
          <Form.Group><Form.Label>Nota (opcional)</Form.Label>
            <Form.Control value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} /></Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setTopupFor(null)}>Cancelar</Button>
          <Button disabled={busy || !(Number(form.amount) > 0)} onClick={async () => {
            if (await run(() => API.post(`/api-services/${topupFor.key}/topup`, { amount: Number(form.amount), at: `${form.at}T12:00:00-04:00`, note: form.note }))) setTopupFor(null);
          }}>Guardar</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!settingsFor} onHide={() => setSettingsFor(null)} centered>
        <Modal.Header closeButton><Modal.Title>Avisos — {settingsFor?.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2"><Form.Label>Avisarme cuando el saldo estimado baje de (US$)</Form.Label>
            <Form.Control type="number" step="0.5" min="0" value={form.lowBalanceAt} onChange={e => setForm(f => ({ ...f, lowBalanceAt: e.target.value }))} /></Form.Group>
          <Form.Group><Form.Label>…o cuando alcance para menos de (días)</Form.Label>
            <Form.Control type="number" step="1" min="0" max="90" value={form.lowDaysAt} onChange={e => setForm(f => ({ ...f, lowDaysAt: e.target.value }))} /></Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSettingsFor(null)}>Cancelar</Button>
          <Button disabled={busy} onClick={async () => {
            if (await run(() => API.put(`/api-services/${settingsFor.key}`, { lowBalanceAt: Number(form.lowBalanceAt), lowDaysAt: Number(form.lowDaysAt) }))) setSettingsFor(null);
          }}>Guardar</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdminApiServicesTab;
